import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useHeatmap } from '@/hooks/useHeatmap'
import { useNavigate } from 'react-router-dom'
import { MapPin, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { addToWatchlist, getMarketsOverview } from '@/lib/api'
import { navigateToTerminalCommand } from '@/lib/navigation'
import type { HeatmapStock, HeatmapSector, MarketsOverviewData } from '@/lib/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number) {
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

function changeColor(change: number): string {
  if (change <= -5) return 'bg-red-700 text-white'
  if (change <= -3) return 'bg-red-600 text-white'
  if (change <= -1) return 'bg-red-500/80 text-white'
  if (change < 0)   return 'bg-red-400/50 text-red-100'
  if (change === 0)  return 'bg-zinc-600 text-zinc-200'
  if (change < 1)   return 'bg-emerald-400/50 text-emerald-100'
  if (change < 3)   return 'bg-emerald-500/80 text-white'
  if (change < 5)   return 'bg-emerald-600 text-white'
  return 'bg-emerald-700 text-white'
}

function changeBorderColor(change: number): string {
  if (change < -1) return 'border-red-800/50'
  if (change < 0) return 'border-red-600/30'
  if (change === 0) return 'border-zinc-700'
  if (change < 1) return 'border-emerald-600/30'
  return 'border-emerald-800/50'
}

function formatCap(m: number): string {
  if (m >= 1_000_000) return `$${(m / 1_000_000).toFixed(1)}T`
  if (m >= 1_000) return `$${(m / 1_000).toFixed(0)}B`
  return `$${m.toFixed(0)}M`
}

// ── Context menu items (matching TickerLink) ───────────────────────────────

const CONTEXT_ITEMS = [
  { label: 'Chart', action: 'chart' },
  { label: 'Quote', action: 'quote' },
  { label: 'Technicals', action: 'technicals' },
  { label: 'Add to Watchlist', action: 'watchlist' },
  { label: 'Analyze', action: 'analyze' },
] as const

// ── Index / Period / View Tabs ─────────────────────────────────────────────

const INDICES = [
  { id: 'all', label: 'All' },
  { id: 'sp500', label: 'S&P 500' },
  { id: 'nasdaq', label: 'NASDAQ' },
  { id: 'ftse100', label: 'FTSE 100' },
]

// NOTE: the 1W/1M/YTD period selector was removed on purpose. The backend
// only has daily change data (price cache change_pct), so the other periods
// silently rendered 1D data while claiming to be period returns. Reintroduce
// a selector only once the backend computes real period returns from history.

const VIEW_MODES = [
  { id: 'stocks', label: 'Stocks' },
  { id: 'commodities', label: 'Commodities' },
  { id: 'forex', label: 'Forex' },
] as const

type ViewMode = typeof VIEW_MODES[number]['id']

// ── Commodities / forex tiles ───────────────────────────────────────────────
// Static skeletons: ticker/name/sector and an illustrative size weight only.
// change_pct + price are hydrated live from /api/markets/overview — the old
// code rendered the hardcoded 0.00% below as if it were market data.

type AltTile = HeatmapStock & { unavailable?: boolean }
type AltSector = HeatmapSector & { stocks: AltTile[]; unavailable?: boolean }

export type AltQuote = { change_pct: number | null; price: number | null }

/** Map /api/markets/overview commodities+forex rows to SYMBOL → quote.
 *  Every field is nullable; rows without a symbol are skipped. */
export function buildAltQuoteMap(
  overview: Partial<MarketsOverviewData> | null | undefined,
): Record<string, AltQuote> {
  const map: Record<string, AltQuote> = {}
  for (const c of overview?.commodities ?? []) {
    if (c?.symbol) {
      map[c.symbol.toUpperCase()] = { change_pct: c.change_pct ?? null, price: c.price ?? null }
    }
  }
  for (const f of overview?.forex ?? []) {
    if (f?.symbol) {
      map[f.symbol.toUpperCase()] = { change_pct: f.change_pct ?? null, price: f.rate ?? null }
    }
  }
  return map
}

const COMMODITY_TILES: HeatmapStock[] = [
  { ticker: 'GC=F', name: 'Gold',          sector: 'Metals',  market_cap: 500, daily_change_pct: 0, price: 0 },
  { ticker: 'SI=F', name: 'Silver',        sector: 'Metals',  market_cap: 200, daily_change_pct: 0, price: 0 },
  { ticker: 'HG=F', name: 'Copper',        sector: 'Metals',  market_cap: 150, daily_change_pct: 0, price: 0 },
  { ticker: 'CL=F', name: 'Crude Oil WTI', sector: 'Energy',  market_cap: 800, daily_change_pct: 0, price: 0 },
  { ticker: 'BZ=F', name: 'Brent Crude',   sector: 'Energy',  market_cap: 700, daily_change_pct: 0, price: 0 },
  { ticker: 'NG=F', name: 'Natural Gas',   sector: 'Energy',  market_cap: 300, daily_change_pct: 0, price: 0 },
  { ticker: 'ZC=F', name: 'Corn',          sector: 'Grains',  market_cap: 120, daily_change_pct: 0, price: 0 },
  { ticker: 'ZW=F', name: 'Wheat',         sector: 'Grains',  market_cap: 100, daily_change_pct: 0, price: 0 },
]

const FOREX_TILES: HeatmapStock[] = [
  { ticker: 'GBPUSD=X', name: 'GBP/USD', sector: 'Major Pairs', market_cap: 500, daily_change_pct: 0, price: 0 },
  { ticker: 'EURUSD=X', name: 'EUR/USD', sector: 'Major Pairs', market_cap: 500, daily_change_pct: 0, price: 0 },
  { ticker: 'USDJPY=X', name: 'USD/JPY', sector: 'Major Pairs', market_cap: 500, daily_change_pct: 0, price: 0 },
  { ticker: 'EURGBP=X', name: 'EUR/GBP', sector: 'Major Pairs', market_cap: 300, daily_change_pct: 0, price: 0 },
  { ticker: 'AUDUSD=X', name: 'AUD/USD', sector: 'Major Pairs', market_cap: 300, daily_change_pct: 0, price: 0 },
  { ticker: 'USDCHF=X', name: 'USD/CHF', sector: 'Major Pairs', market_cap: 300, daily_change_pct: 0, price: 0 },
  { ticker: 'USDCAD=X', name: 'USD/CAD', sector: 'Major Pairs', market_cap: 300, daily_change_pct: 0, price: 0 },
]

// ── Stock Cell ──────────────────────────────────────────────────────────────

function StockCell({ stock, totalCap, onClick, onContextMenu }: {
  stock: AltTile
  totalCap: number
  onClick: () => void
  onContextMenu: (e: React.MouseEvent) => void
}) {
  const weight = stock.market_cap / totalCap
  const minPx = 60
  // unavailable = the live quote fetch failed / had no data — show '—', not
  // a fabricated 0.00%
  const changeLabel = stock.unavailable ? '—' : pct(stock.daily_change_pct)

  return (
    <button
      type="button"
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={clsx(
        'relative flex flex-col items-center justify-center border rounded-sm transition-all',
        'hover:brightness-125 hover:z-10 hover:shadow-lg cursor-pointer',
        'overflow-hidden',
        changeColor(stock.unavailable ? 0 : stock.daily_change_pct),
        changeBorderColor(stock.unavailable ? 0 : stock.daily_change_pct),
      )}
      style={{
        flexGrow: Math.max(weight * 1000, 1),
        flexBasis: `${minPx}px`,
        minWidth: `${minPx}px`,
        minHeight: '56px',
      }}
      title={`${stock.name} (${stock.ticker})\n${formatCap(stock.market_cap)} market cap\n${changeLabel}`}
    >
      <span className="text-xs font-bold leading-none">{stock.ticker}</span>
      <span className="text-[10px] leading-none mt-0.5 opacity-90">{changeLabel}</span>
    </button>
  )
}

// ── Sector Row ──────────────────────────────────────────────────────────────

function SectorRow({ sector, totalCap, onStockClick, onStockContextMenu }: {
  sector: AltSector
  totalCap: number
  onStockClick: (ticker: string) => void
  onStockContextMenu: (ticker: string, e: React.MouseEvent) => void
}) {
  const sectorWeight = sector.market_cap / totalCap

  return (
    <div
      className="flex flex-col"
      style={{
        flexGrow: Math.max(sectorWeight * 100, 1),
        flexBasis: '120px',
        minWidth: '120px',
      }}
    >
      {/* Sector label */}
      <div className={clsx(
        'px-2 py-1 text-xs font-semibold border-b flex items-center justify-between',
        'bg-surface-card border-border text-text-secondary',
      )}>
        <span>{sector.sector}</span>
        <span className={clsx(
          'text-[10px] font-mono',
          sector.unavailable ? 'text-text-muted'
            : sector.avg_change_pct >= 0 ? 'text-emerald-400' : 'text-rose-400',
        )}>
          {sector.unavailable ? '—' : pct(sector.avg_change_pct)}
        </span>
      </div>
      {/* Stock cells */}
      <div className="flex flex-wrap flex-1 gap-px p-px bg-surface-DEFAULT/30">
        {sector.stocks.map(stock => (
          <StockCell
            key={stock.ticker}
            stock={stock}
            totalCap={sector.market_cap}
            onClick={() => onStockClick(stock.ticker)}
            onContextMenu={(e) => onStockContextMenu(stock.ticker, e)}
          />
        ))}
      </div>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function HeatmapPage() {
  const { data, loading, error, index, setIndex, fetchHeatmap } = useHeatmap()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('stocks')

  // Context menu state
  const [ctxMenu, setCtxMenu] = useState<{ ticker: string; x: number; y: number } | null>(null)
  const ctxRef = useRef<HTMLDivElement>(null)

  // Transient toast for add-to-watchlist outcomes (success / duplicate / failure)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 3500)
  }, [])
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
  }, [])

  // Live quotes for the commodities/forex tiles, keyed by symbol.
  // null = not fetched (or failed) — tiles render '—' rather than a fake 0%.
  const [altQuotes, setAltQuotes] = useState<Record<string, AltQuote> | null>(null)
  const [altQuotesFailed, setAltQuotesFailed] = useState(false)

  useEffect(() => {
    fetchHeatmap()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewMode === 'stocks') return
    let cancelled = false
    getMarketsOverview()
      .then(overview => {
        if (cancelled) return
        setAltQuotes(buildAltQuoteMap(overview))
        setAltQuotesFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        setAltQuotes(null)
        setAltQuotesFailed(true)
      })
    return () => { cancelled = true }
  }, [viewMode])

  const totalCap = useMemo(() => {
    if (!data) return 0
    return data.sectors.reduce((sum, s) => sum + s.market_cap, 0)
  }, [data])

  // Navigate to chart page on click
  function handleStockClick(ticker: string) {
    navigate(`/app/chart?ticker=${ticker}`)
  }

  // Show context menu on right-click
  function handleStockContextMenu(ticker: string, e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ ticker, x: e.clientX, y: e.clientY })
  }

  // Handle context menu action
  const handleCtxAction = useCallback((action: string) => {
    if (!ctxMenu) return
    const ticker = ctxMenu.ticker
    setCtxMenu(null)
    switch (action) {
      case 'chart':
        navigate(`/app/chart?ticker=${ticker}`)
        break
      case 'quote':
        navigateToTerminalCommand(navigate, `/quote ${ticker}`)
        break
      case 'technicals':
        navigateToTerminalCommand(navigate, `/technicals ${ticker}`)
        break
      case 'watchlist':
        // Surface the outcome — silently swallowing a 400 (already on the
        // watchlist) made the button look broken.
        addToWatchlist({ ticker, name: ticker })
          .then(() => showToast(`${ticker} added to watchlist`))
          .catch((err: any) => {
            const detail = err?.response?.data?.detail
            if (typeof detail === 'string' && detail) {
              showToast(detail)
            } else {
              showToast(`Failed to add ${ticker} to watchlist`)
            }
          })
        break
      case 'analyze':
        navigateToTerminalCommand(navigate, `/analyze ${ticker}`)
        break
    }
  }, [ctxMenu, navigate, showToast])

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!ctxMenu) return
    const handleClick = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setCtxMenu(null)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCtxMenu(null)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [ctxMenu])

  // Build commodity/forex pseudo-sectors, hydrated with live change/price
  // from /api/markets/overview. Tiles with no quote are flagged unavailable.
  const altSectors: AltSector[] = useMemo(() => {
    const hydrate = (tiles: HeatmapStock[]): AltTile[] =>
      tiles.map(t => {
        const q = altQuotes?.[t.ticker.toUpperCase()]
        if (q && q.change_pct != null) {
          return { ...t, daily_change_pct: q.change_pct, price: q.price ?? 0 }
        }
        return { ...t, daily_change_pct: 0, price: 0, unavailable: true }
      })
    const toSector = (name: string, stocks: AltTile[]): AltSector => {
      const withData = stocks.filter(s => !s.unavailable)
      return {
        sector: name,
        market_cap: stocks.reduce((s, st) => s + st.market_cap, 0),
        avg_change_pct: withData.length
          ? withData.reduce((s, st) => s + st.daily_change_pct, 0) / withData.length
          : 0,
        unavailable: withData.length === 0,
        stocks,
      }
    }
    if (viewMode === 'commodities') {
      const groups: Record<string, AltTile[]> = {}
      for (const c of hydrate(COMMODITY_TILES)) {
        ;(groups[c.sector] ??= []).push(c)
      }
      return Object.entries(groups).map(([sector, stocks]) => toSector(sector, stocks))
    }
    if (viewMode === 'forex') {
      return [toSector('Major FX Pairs', hydrate(FOREX_TILES))]
    }
    return []
  }, [viewMode, altQuotes])

  const displaySectors = viewMode === 'stocks' ? (data?.sectors ?? []) : altSectors
  const displayTotalCap = viewMode === 'stocks'
    ? totalCap
    : displaySectors.reduce((s, sec) => s + sec.market_cap, 0)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <MapPin size={18} className="text-violet-400" />
          </div>
          <div>
            <h1 className="page-title">Market Heatmap</h1>
            <p className="text-xs text-text-muted">Click tile for chart, right-click for more options</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => fetchHeatmap()}
          className="btn-secondary p-2"
          aria-label="Refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      {/* Controls */}
      <div className="glass px-6 py-3 flex items-center gap-6 shrink-0 flex-wrap">
        {/* View mode toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-text-muted mr-2">View:</span>
          {VIEW_MODES.map(v => (
            <button
              type="button"
              key={v.id}
              onClick={() => setViewMode(v.id)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                viewMode === v.id
                  ? 'bg-violet-500/20 text-violet-400'
                  : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
              )}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Index filter (only for stocks view) */}
        {viewMode === 'stocks' && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-text-muted mr-2">Index:</span>
            {INDICES.map(i => (
              <button
                type="button"
                key={i.id}
                onClick={() => setIndex(i.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  index === i.id
                    ? 'bg-blue/20 text-blue-light'
                    : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                )}
              >
                {i.label}
              </button>
            ))}
          </div>
        )}

        {/* Daily change only — see the note above VIEW_MODES for why there is
            no period selector. */}
        {viewMode === 'stocks' && (
          <span className="text-xs text-text-muted">Change shown: 1 day</span>
        )}
      </div>

      {/* Heatmap content */}
      <div className="flex-1 overflow-auto p-4">
        {loading && !data && viewMode === 'stocks' && (
          <div className="skeleton h-64" />
        )}

        {error && viewMode === 'stocks' && (
          <div className="flex items-center justify-center h-64">
            <p className="text-rose-400 text-sm">{error}</p>
          </div>
        )}

        {displaySectors.length > 0 && (
          <div className="card p-1.5 flex flex-wrap gap-1 min-h-[400px]">
            {displaySectors.map(sector => (
              <SectorRow
                key={sector.sector}
                sector={sector}
                totalCap={displayTotalCap}
                onStockClick={handleStockClick}
                onStockContextMenu={handleStockContextMenu}
              />
            ))}
          </div>
        )}

        {viewMode !== 'stocks' && (
          <p className="text-xs text-text-muted mt-3">
            Tile sizes are illustrative weights; change % is live daily data.
            {altQuotesFailed
              ? ' Live quotes could not be loaded — tiles showing "—".'
              : ' Tiles showing "—" have no quote available.'}
            {' '}Click any tile to open its chart, or right-click for more options.
          </p>
        )}

        {viewMode === 'stocks' && data && data.sectors.length === 0 && (
          <div className="flex items-center justify-center h-64">
            <p className="text-text-muted text-sm">No data available for this selection.</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 py-2 border-t border-border flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-text-muted mr-1">Change:</span>
        {[
          { label: '-5%+', cls: 'bg-red-700' },
          { label: '-3%', cls: 'bg-red-600' },
          { label: '-1%', cls: 'bg-red-500/80' },
          { label: '0%', cls: 'bg-zinc-600' },
          { label: '+1%', cls: 'bg-emerald-500/80' },
          { label: '+3%', cls: 'bg-emerald-600' },
          { label: '+5%+', cls: 'bg-emerald-700' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className={clsx('w-4 h-3 rounded-sm', l.cls)} />
            <span className="text-[10px] text-text-muted">{l.label}</span>
          </div>
        ))}
      </div>

      {/* Transient toast (watchlist outcomes) */}
      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 rounded-lg
                     bg-surface-card border border-border shadow-xl text-sm text-text-primary animate-fade-in"
        >
          {toast}
        </div>
      )}

      {/* Context menu (portal-style) */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          className="fixed z-[9999] min-w-[160px] py-1 bg-surface-card border border-border rounded-lg shadow-xl animate-fade-in"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-muted font-semibold border-b border-border">
            {ctxMenu.ticker}
          </div>
          {CONTEXT_ITEMS.map(item => (
            <button
              type="button"
              key={item.action}
              onClick={() => handleCtxAction(item.action)}
              className="w-full text-left px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
